
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import { Classname } from "@rivtn/noseur";
import Btn from "./Btn";
import { memberRole } from "./MemberCard";
import { i18nManager } from "../../i18n";
import { Utils } from "../../../../../utils";
import { ITeamMember } from "../../../../../services";
import styles from "../../assets/style.module.css";

export interface MemberModalProps {
    member: ITeamMember;
    onClose: () => void;
}

function MemberModal({ member: m, onClose }: MemberModalProps) {

    const labels = i18nManager.Labels.members;
    const role = memberRole(m);
    const achievements: string[] = m.extra_data?.recent_personal_achievements ?? [];
    const info: [string, string | undefined][] = [
        [labels.role, m.extra_data?.team_role],
        [labels.country, m.settings?.country],
        [labels.region, m.settings?.state_region],
        [labels.joined, m.join_date ? Utils.fineDate(new Date(m.join_date)) : undefined],
        [labels.left, m.exit_date ? Utils.fineDate(new Date(m.exit_date)) : undefined],
    ];

    React.useEffect(() => {
        const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [onClose]);

    return (<div className={styles["modal-overlay"]} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className={styles["modal"]} role="dialog" aria-label={m.display_name}>
            <button className={styles["modal-close"]} onClick={onClose} aria-label="Close"><i className="fa fa-times" /></button>
            <div className={styles["modal-media"]}>
                <img src={m.half_body_picture_url || m.passport_picture_url} alt={m.display_name} />
            </div>
            <div className={styles["modal-body"]}>
                <span className={Classname.build(styles["role-pill"], styles[`role-${role.key}`])}>{role.label}</span>
                <h2 className={styles["d3"]}>{m.display_name}</h2>
                <span className={Classname.build(styles["status"], { [styles["status-active"]]: m.status === "ACTIVE" })}><i className="fa fa-circle" style={{ fontSize: "0.55em", verticalAlign: "middle" }} /> {m.status === "ACTIVE" ? labels.active : labels.former_badge}</span>
                {m.profile_details && (<p className={styles["modal-quote"]}>“{m.profile_details}”</p>)}

                <dl className={styles["modal-info"]}>
                    {info.filter(([_, v]) => !!v).map(([k, v]) => (<div key={k}>
                        <dt>{k}</dt>
                        <dd>{v}</dd>
                    </div>))}
                </dl>

                {Object.keys(m.social_accounts ?? {}).length > 0 && (<>
                    <div className={styles["modal-label"]}>{labels.socials}</div>
                    <div className={styles["modal-socials"]}>
                        {Object.entries(m.social_accounts).map(([social, username]) => (
                            <ReactRouterDom.NavLink key={social} target="_blank" to={Utils.getSocialLink(social, username)} className={styles["social-pill"]}>
                                <i className={Utils.getSocialIcon(social)} /> {username}
                            </ReactRouterDom.NavLink>
                        ))}
                    </div>
                </>)}

                {achievements.length > 0 && (<>
                    <div className={styles["modal-label"]}>{labels.achievements}</div>
                    <div className={styles["modal-tags"]}>
                        {achievements.map((a) => (<span key={a} className={styles["tag"]}>{a}</span>))}
                    </div>
                </>)}

                {m.settings?.show_link_to_profile && (<div style={{ marginTop: "2rem" }}>
                    <Btn size="sm" variant="outline" target="_blank" href={`${window.location.origin}/me/${m.member_id}`}>{labels.view_profile}</Btn>
                </div>)}
            </div>
        </div>
    </div>);
}

export default MemberModal;
